import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/api/apiClient";
import { type GraphBuildJob, getGraphBuildJobStatus } from "@/api/graphBuilderApi";

interface UseGraphBuildJobPollingOptions {
	onGraphGenerated?: (graphId: string) => void;
}

interface UseGraphBuildJobPollingReturn {
	currentJobId: string | null;
	setCurrentJobId: (jobId: string | null) => void;
	jobStatus: GraphBuildJob | undefined;
	clearJob: () => void;
}

const STORAGE_KEY = "graphBuildJobId";
const POLLING_TIMEOUT_MS = 500 * 1000; // 500 seconds

export const useGraphBuildJobPolling = ({
	onGraphGenerated,
}: UseGraphBuildJobPollingOptions): UseGraphBuildJobPollingReturn => {
	const queryClient = useQueryClient();
	const [currentJobId, setCurrentJobIdState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

	const setCurrentJobId = useCallback((jobId: string | null) => {
		setCurrentJobIdState(jobId);
		if (jobId) localStorage.setItem(STORAGE_KEY, jobId);
		else localStorage.removeItem(STORAGE_KEY);
	}, []);

	const { data: jobStatus } = useQuery<GraphBuildJob>({
		queryKey: ["graphBuildJobStatus", currentJobId],
		queryFn: ({ signal }) => {
			if (!currentJobId) throw new Error("No job ID");
			return getGraphBuildJobStatus(currentJobId, { signal });
		},
		enabled: !!currentJobId,
		retry: (failureCount, error) => {
			// Job not found (server restarted) — clear storage and stop
			if (error instanceof ApiError && error.status === 404) {
				setCurrentJobId(null);
				return false;
			}
			return failureCount < 2;
		},
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data?.status === "completed" || data?.status === "failed") {
				return false;
			}

			// Check if 500 seconds have elapsed since job creation
			if (data?.createdAt) {
				const createdAt = new Date(data.createdAt).getTime();
				const now = Date.now();
				const elapsed = now - createdAt;
				if (elapsed >= POLLING_TIMEOUT_MS) {
					return false; // Stop polling after timeout
				}
			}

			const attempt = query.state.dataUpdateCount;
			if (attempt < 10) return 2000;
			if (attempt < 30) return 4000;
			if (attempt < 60) return 8000;
			return 15000;
		},
		refetchIntervalInBackground: false,
	});

	useEffect(() => {
		if (jobStatus?.status === "completed" && jobStatus.graphId) {
			onGraphGenerated?.(jobStatus.graphId);
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphs"] });
			queryClient.invalidateQueries({ queryKey: ["workspaces"] });
			queryClient.invalidateQueries({ queryKey: ["workspace"] });
			setCurrentJobId(null);
		} else if (jobStatus?.status === "failed") {
			setCurrentJobId(null);
		}
	}, [jobStatus, onGraphGenerated, queryClient, setCurrentJobId]);

	return {
		currentJobId,
		setCurrentJobId,
		jobStatus,
		clearJob: () => setCurrentJobId(null),
	};
};

export default useGraphBuildJobPolling;
